import { Common, color } from '@stockbit/ui';
import { RefObject, createRef, useEffect, useRef, useState } from 'react';
import If from 'global/ErrorHandlers/If';

import { clevertapSecuritiesTradingPinSubmitted } from 'lib/analytics/securities';
import { LoginInputNumber } from './styled';

const { Flex, Text } = Common;

const KEY_CODE_BACKSPACE = 8;
const KEY_CODE_DELETE = 46;

interface LoginInputPinProps {
  onLogin: (pin: string) => void;
  errorMsg?: string;
}

export default function LoginInputPin({
  onLogin,
  errorMsg,
}: LoginInputPinProps) {
  const inputRef = useRef<RefObject<HTMLInputElement>[]>(
    [...Array(6)].map(() => createRef()),
  );

  const [inputValue, setInputValue] = useState([...Array(6)]);

  useEffect(() => {
    inputRef.current[0].current.focus();

    return () => setInputValue([...Array(6)]);
  }, []);

  const onChangeInput = (e) => (index) => {
    const { value } = e.target;
    const newInputValue = [...inputValue];

    if (newInputValue.join('').length === 6) return;

    if (e.keyCode !== KEY_CODE_BACKSPACE && e.keyCode !== KEY_CODE_DELETE) {
      // eslint-disable-next-line prefer-destructuring
      newInputValue[index] = value[0];
      setInputValue(newInputValue);

      window.requestAnimationFrame(() => {
        if (index < 5 && inputRef.current[index].current.value !== '') {
          inputRef.current[index + 1].current.focus();
        }

        if (
          index === 5 &&
          newInputValue[5] !== undefined &&
          newInputValue.join('').length === 6
        ) {
          onLogin(newInputValue.join(''));
          clevertapSecuritiesTradingPinSubmitted();
        }
      });
    }

    if (e.keyCode === KEY_CODE_BACKSPACE) {
      newInputValue[index] = '';
      setInputValue(newInputValue);

      window.requestAnimationFrame(() => {
        if (index > 0 && index <= 5) {
          inputRef.current[index - 1].current.focus();
        }
      });
    }
  };

  const onKeyDown = (e) => (index) => {
    const numKeyValue = Number(e.key);
    const newInputValue = [...inputValue];

    if (Number.isNaN(numKeyValue) && e.keyCode !== KEY_CODE_BACKSPACE) {
      e.preventDefault();
      return;
    }

    if (e.keyCode === KEY_CODE_BACKSPACE) {
      newInputValue[index] = '';
      setInputValue(newInputValue);

      window.requestAnimationFrame(() => {
        if (index > 0 && index <= 5) {
          inputRef.current[index - 1].current.focus();
        }
      });
    }
  };

  return (
    <>
      <Flex justify="center" width="100%">
        {[...Array(6)].map((_, i) => (
          <LoginInputNumber
            data-cy={`input-pin-${i}`}
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            type="password"
            name="auth-modal-input-pin"
            autoComplete="new-password"
            autoFocus
            ref={inputRef.current[i]}
            value={inputValue[i]}
            onChange={(e) => onChangeInput(e)(i)}
            onKeyDown={(e) => onKeyDown(e)(i)}
            onFocus={(e) => e.target.select()}
          />
        ))}
      </Flex>
      <If condition={errorMsg !== ''}>
        <Text
          align="center"
          color={color.sunsetOrange}
          mt="24px"
          data-cy="input-pin-error"
        >
          {errorMsg}
        </Text>
      </If>
    </>
  );
}

LoginInputPin.defaultProps = {
  errorMsg: '',
};
